import { TreeViewBaseItem } from "@mui/x-tree-view-pro";
import insert from "ramda/es/insert";
import { isNotNil, isNotNilOrEmpty, isNull } from "ramda-adjunct";
import { PositionedItemTree } from "@sinch/components/application/RatelistBuilder/BuilderTree/types";

/**
 * Inserts a new item into a tree structure at the specified position.
 *
 * @param tree - The tree to insert the new item into
 * @param parentId - The ID of the parent node, or null for root level
 * @param newItem - The new item to insert
 * @param [index] - Optional index position for insertion, defaults to end of the array
 * @returns The updated tree or null if parent not found
 */
export const insertIntoTree = <TreeItem extends TreeViewBaseItem>(
  tree: TreeItem[],
  parentId: string | null,
  newItem: TreeItem,
  index?: number
): TreeItem[] | null => {
  // If no parent, use root
  if (parentId === null) {
    return insert(index ?? Infinity, newItem, tree);
  }

  for (const node of tree) {
    if (node.id === parentId) {
      if (!node.children) {
        node.children = [];
      }
      node.children = insert(index ?? Infinity, newItem, node.children);
      return [...tree];
    } else if (node.children) {
      const result = insertIntoTree<TreeItem>(node.children as TreeItem[], parentId, newItem, index);
      if (result) {
        return [...tree];
      }
    }
  }
  return null;
};

/**
 * Removes a node from the tree structure by its ID.
 *
 * @template TreeItem - Type extending TreeViewBaseItem
 * @param tree - The tree to remove the node from
 * @param id - The ID of the node to remove
 * @returns The updated tree or null if node not found
 */
export const removeFromTree = <TreeItem extends TreeViewBaseItem>(tree: TreeItem[], id: string): TreeItem[] | null => {
  for (let i = 0; i < tree.length; i++) {
    if (tree[i].id === id) {
      tree.splice(i, 1);
      return [...tree];
    } else if (isNotNil(tree[i].children)) {
      const nodeChildren = tree[i].children as TreeItem[];
      const result = removeFromTree<TreeItem>(nodeChildren!, id);
      if (result) {
        return [...tree];
      }
    }
  }
  return null;
};

/**
 * Copies a node in the tree and inserts the copy next to the original.
 *
 * @template TreeItem - Type extending TreeViewBaseItem
 * @param  tree - The tree to copy the node in
 * @param nodeId - The ID of the node to copy
 * @param getNewId - A function that generates a new ID for the copied node
 * @returns The updated tree
 */
export const copyNodeInTree = <TreeItem extends TreeViewBaseItem>(
  tree: TreeItem[],
  nodeId: string,
  getNewId: (newId: string) => string
): TreeItem[] | null => {
  for (let ix = 0; ix < tree.length; ix++) {
    const node = tree[ix];
    if (node.id === nodeId) {
      const newNode = updateNodeIds([{ ...node }], getNewId);
      return [...tree.slice(0, ix + 1), ...newNode, ...tree.slice(ix + 1)];
    } else if (isNotNilOrEmpty(node.children)) {
      const result = copyNodeInTree<TreeItem>(node.children as TreeItem[], nodeId, getNewId);
      if (result) {
        node.children = result;
        return [...tree];
      }
    }
  }
  return null;
};

/**
 * Updates the IDs of all nodes in a tree using a callback function.
 *
 * @template TreeItem - Type extending TreeViewBaseItem
 * @param tree - The tree to update node IDs in
 * @param updateIdCallback - A function that maps an existing ID to a new ID
 * @returns The updated tree with new IDs
 */
export const updateNodeIds = <TreeItem extends TreeViewBaseItem>(
  tree: TreeItem[],
  updateIdCallback: (id: string) => string
): TreeItem[] =>
  tree.map((node) => {
    const updatedNode = {
      ...node,
      id: updateIdCallback(node.id),
    };

    if (node.children) {
      updatedNode.children = updateNodeIds(node.children as TreeItem[], updateIdCallback);
    }

    return updatedNode;
  });

/**
 * Finds a node in the tree by its ID.
 *
 * @template TreeItem - Type extending TreeViewBaseItem
 * @param tree - The tree to search in
 * @param nodeId - The ID of the node to find
 * @returns The found node or null if not found
 */
export const getNode = <TreeItem extends TreeViewBaseItem>(tree: TreeItem[], nodeId: string): TreeItem | null => {
  for (const node of tree) {
    if (node.id === nodeId) {
      return node;
    } else if (node.children) {
      const result = getNode<TreeItem>(node.children as TreeItem[], nodeId);
      if (result) {
        return result;
      }
    }
  }
  return null;
};

/**
 * Moves a node from its current position to a new parent in the tree.
 *
 * @template TreeItem - Type extending TreeViewBaseItem
 * @param tree - The tree to perform the move in
 * @param nodeId - The ID of the node to move
 * @param newParentId - The ID of the new parent, or null for root level
 * @param [index] - Optional index position for insertion, defaults to end
 * @returns The updated tree or null if operation failed
 */
export const moveNodeInTree = <TreeItem extends TreeViewBaseItem>(
  tree: TreeItem[],
  nodeId: string,
  newParentId: string | null,
  index?: number
): TreeItem[] | null => {
  const nodeToMove = getNode<TreeItem>(tree, nodeId);
  if (!nodeToMove) {
    return null;
  }

  // Remove the node from its current parent
  const treeWithoutNode = removeFromTree<TreeItem>(tree, nodeId);
  if (!treeWithoutNode) {
    return null;
  }

  // Insert the node into the new parent
  return insertIntoTree<TreeItem>(treeWithoutNode, newParentId, nodeToMove, index);
};

/**
 * Finds the parent node of a node with the specified ID.
 *
 * @template TreeItem - Type extending TreeViewBaseItem
 * @param  tree - The tree to search in
 * @param nodeId - The ID of the node whose parent to find
 * @returns The parent node or null if not found
 */
export const getParentNodeById = <TreeItem extends TreeViewBaseItem>(
  tree: TreeItem[],
  nodeId: string
): TreeItem | null => {
  for (const node of tree) {
    if (node.children) {
      for (const child of node.children) {
        if (child.id === nodeId) {
          return node;
        }
      }
      const result = getParentNodeById<TreeItem>(node.children as TreeItem[], nodeId) as TreeItem | null;
      if (result) {
        return result as TreeItem;
      }
    }
  }
  return null;
};

export const getNodePath = <TreeItem extends TreeViewBaseItem>(tree: TreeItem[], nodeId: string): string[] => {
  let currentNode = nodeId;
  let parent;
  const path = [nodeId];
  while ((parent = getParentNodeById(tree, currentNode))) {
    path.push(parent.id);
    currentNode = parent.id;
  }
  return path.toReversed();
};

/**
 * Finds index whithin parent node
 *
 * @template TreeItem - Type extending TreeViewBaseItem
 * @param  tree - The tree to search in
 * @param nodeId - The ID of the node whose index to find
 * @returns index or null if not found
 */
export const getIndexOfNodeById = <TreeItem extends TreeViewBaseItem>(
  tree: TreeItem[],
  nodeId: string
): number | null => {
  const parent = getParentNodeById(tree, nodeId);
  const childrens = isNull(parent) ? tree : parent.children;

  const result = (childrens ?? []).findIndex((node) => node.id === nodeId);
  return result === -1 ? null : result;
};

/**
 * Retrieves all IDs from nodes in the tree in a flattened array.
 *
 * @template TreeItem - Type extending TreeViewBaseItem
 * @param tree - The tree to extract IDs from
 * @returns Array of all node IDs in the tree
 */
export const getTreeIds = <TreeItem extends TreeViewBaseItem>(tree: TreeItem[]): string[] => {
  let result: string[] = [];
  for (const node of tree) {
    result = [...result, node.id, ...(node.children ? getTreeIds(node.children as TreeItem[]) : [])];
  }
  return result;
};

/**
 * Collects the ancestor node IDs of a given node in a tree structure.
 */
export const collectAncestorIds = <TreeItem extends TreeViewBaseItem>(tree: TreeItem[], nodeId: string): string[] => {
  const ancestorIds: string[] = [];
  let parent: TreeItem | null = getParentNodeById(tree, nodeId);
  while (parent) {
    ancestorIds.push(parent.id);
    parent = getParentNodeById(tree, parent.id);
  }
  return ancestorIds;
};

/**
 * Retrieves all IDs from nodes which have no children in the tree in a flattened array.
 *
 * @template TreeItem - Type extending TreeViewBaseItem
 * @param tree - The tree to extract IDs from
 * @returns Array of all node IDs in the tree
 */
export const getTreeLeafsIds = <TreeItem extends TreeViewBaseItem>(tree: TreeItem[]): string[] => {
  let result: string[] = [];
  for (const node of tree) {
    result = [...result, ...(node.children ? getTreeLeafsIds(node.children as TreeItem[]) : [node.id])];
  }
  return result;
};
export const findItemInTree = <T extends TreeViewBaseItem>(
  tree: T[],
  itemId: string,
  parent: T | null = null
): PositionedItemTree<T> | null => {
  for (let i = 0; i < tree.length; i++) {
    const item = tree[i];
    if (item.id === itemId) {
      const lastOrBetween = i === tree.length - 1 ? "last" : "between";
      return { item, parent, index: i, position: i === 0 ? "first" : lastOrBetween };
    }
    if (item.children) {
      const result = findItemInTree<T>(item.children as T[], itemId, item);

      if (result) {
        return result;
      }
    }
  }
  return null;
};
